import React from "react";
import { Linkedin, Instagram, Facebook, Mail } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_appointments-engine/artifacts/w44rfe3v_logo.png";

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      style={{
        position: "relative",
        background: "rgba(5,5,5,0.70)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "80px 24px 40px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "48px" }}>
          {/* Brand column */}
          <div style={{ gridColumn: "span 2" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
              <img
                src={LOGO_URL}
                alt="TMN Resource Solutions"
                style={{ width: "48px", height: "48px", objectFit: "contain" }}
              />
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#fff",
                  letterSpacing: "0.01em",
                }}
              >
                TMN{" "}
                <span style={{ color: "#e8c45a", fontStyle: "italic" }}>Resource Solutions</span>
              </span>
            </div>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "13px",
                fontWeight: 300,
                color: "rgba(255,255,255,0.4)",
                lineHeight: 1.7,
                letterSpacing: "0.01em",
                maxWidth: "360px",
                marginBottom: "24px",
              }}
            >
              Trained cold callers, appointment setters, and customer service agents —
              built around accountability, measured performance, and scalable teams.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              {[
                { icon: Linkedin, href: "#", label: "LinkedIn" },
                { icon: Instagram, href: "#", label: "Instagram" },
                { icon: Facebook, href: "#", label: "Facebook" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "rgba(255,255,255,0.3)",
                    textDecoration: "none",
                    transition: "all 0.25s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(210,170,60,0.4)";
                    e.currentTarget.style.color = "#e8c45a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.3)";
                  }}
                >
                  <s.icon style={{ width: "16px", height: "16px" }} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigate column */}
          <div>
            <div
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.4em",
                color: "rgba(210,170,60,0.6)",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              Navigate
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
              {[
                { href: "#services", label: "Services" },
                { href: "#why-us", label: "Why Us" },
                { href: "#how-it-works", label: "Process" },
                { href: "#performance", label: "Performance" },
                { href: "#contact", label: "Contact" },
              ].map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={scrollToSection(l.href)}
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: "12px",
                      fontWeight: 300,
                      color: "rgba(255,255,255,0.4)",
                      textDecoration: "none",
                      letterSpacing: "0.05em",
                      transition: "color 0.25s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#e8c45a")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Get In Touch column */}
          <div>
            <div
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.4em",
                color: "rgba(210,170,60,0.6)",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              Get In Touch
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
              <li style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Mail style={{ width: "14px", height: "14px", color: "rgba(255,255,255,0.3)", marginTop: "2px", flexShrink: 0 }} />
                <a
                  href="mailto:management@tmnresourcesolutions.com"
                  data-testid="footer-email"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "12px",
                    fontWeight: 300,
                    color: "rgba(255,255,255,0.4)",
                    textDecoration: "none",
                    letterSpacing: "0.02em",
                    transition: "color 0.25s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#e8c45a")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
                >
                  management@tmnresourcesolutions.com
                </a>
              </li>
              <li>
                <a
                  href="#booking"
                  onClick={scrollToSection("#booking")}
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "12px",
                    fontWeight: 300,
                    color: "rgba(255,255,255,0.4)",
                    textDecoration: "none",
                    letterSpacing: "0.02em",
                    transition: "color 0.25s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#e8c45a")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
                >
                  Book a Strategy Call →
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            marginTop: "64px",
            paddingTop: "32px",
            borderTop: "1px solid rgba(255,255,255,0.04)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "10px",
              color: "rgba(255,255,255,0.25)",
              letterSpacing: "0.05em",
            }}
          >
            &copy; {new Date().getFullYear()} TMN Resource Solutions. All rights reserved.
          </div>
          <div
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "9px",
              letterSpacing: "0.3em",
              color: "rgba(200,160,50,0.3)",
              textTransform: "uppercase",
            }}
          >
            Connecting Vision & Success
          </div>
          <a
            href="/admin"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "9px",
              color: "rgba(255,255,255,0.15)",
              textDecoration: "none",
              letterSpacing: "0.05em",
              transition: "color 0.25s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.15)")}
          >
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
